import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Input from '../input';
import CalendarToday from '../icons/internals/CalendarToday';
import Calendar from './Calendar';
import Button from '../button';
import Tooltip from '../tooltip';

function getPanelDate(value) {
  if (moment(value[0]).isSameOrAfter(moment(value[1]), 'month')) {
    return {
      startPanelDate: moment(value[0]),
      endPanelDate: moment(value[0]).add(1, 'month'),
    };
  }
  return {
    startPanelDate: value[0] ? value[0] : moment(value[0]),
    endPanelDate: value[1] ? value[1] : moment(value[1]),
  };
}
class DateRangePicker extends Component {
  static propTypes = {
    /**
     * 类前缀
     * */
    prefixCls: PropTypes.string,
    /**
     * 分隔符
     * */
    separator: PropTypes.node,
    /**
     * 选择类型
     * */
    selectType: PropTypes.oneOf(['date', 'month', 'year']),
    /**
     * 是否禁用
     */
    disabled: PropTypes.bool,
    /**
     * 日期显示格式
     */
    format: PropTypes.string,
    /**
     * 是否可以选择时刻
     */
    showTime: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    /**
     * 后缀
     */
    suffix: PropTypes.node,
    /**
     * 占位文本
     */
    placeholder: PropTypes.node,
    /**
     * input元素的值，受控模式下使用
     */
    defaultValue: PropTypes.arrayOf(PropTypes.object),
    /**
     * 不可选择的时间范围
     */
    disabledTime: PropTypes.func,
    /**
     *  scrollContainer Tooltip兼容自定义可滚动容器
     */
    scrollContainer: PropTypes.node,
  };

  static defaultProps = {
    disabled: false,
    format: 'YYYY-MM-DD',
    clearable: false,
    separator: '~',
    selectType: 'date',
    showTime: false,
    suffix: <CalendarToday />,
    prefixCls: 'ne-date-range-picker',
    defaultValue: [],
    scrollContainer: '',
  };

  static getDerivedStateFromProps(props, state) {
    const { defaultValue } = props;
    if (defaultValue !== state.prevDefaultValue) {
      return {
        value: defaultValue,
        prevDefaultValue: defaultValue,
      };
    }
    return null;
  }

  constructor(props) {
    super(props);
    const { defaultValue, selectType } = props;

    this.state = {
      prevDefaultValue: defaultValue,
      value: defaultValue,
      panelType: selectType,
      ...getPanelDate(defaultValue),
    };
  }

  handleValueChange = date => {
    const { onChange } = this.props;
    if (this.inputRef) this.inputRef.focus();
    let newValue = [];
    this.setState(state => {
      if (state.value.length === 0 || state.value.length === 2) {
        newValue = [date];
      } else if (!state.value[0].isSame(date)) {
        newValue = [state.value[0], date];
      } else {
        newValue = [state.value[0], moment(date).add(1, 'second')];
      }
      newValue.sort((a, b) => (a > b ? 1 : -1));
      if (onChange && newValue.length !== 1) {
        onChange(newValue);
      }
      return {
        value: newValue,
      };
    });
  };

  handleTimeChange = (time, type) => {
    const { onChange } = this.props;
    if (this.inputRef) this.inputRef.focus();
    this.setState(state => {
      const newValue = [...state.value];
      if (type === 'start') {
        newValue[0] = time;
      } else {
        newValue[1] = time;
      }
      if (onChange && newValue.length !== 1) {
        onChange(newValue);
      }
      return {
        value: newValue,
      };
    });
  };

  handlePanelTypeChange = type => {
    this.setState({
      panelType: type,
    });
  };

  handleClose = () => {
    const { value } = this.state;
    const { onChange } = this.props;
    if (value && value.length !== 2) {
      if (onChange) {
        onChange([]);
      }
      this.setState({
        value: [],
        ...getPanelDate([]),
      });
    }
  };

  handleClear = () => {
    const { onChange } = this.props;
    if (onChange) {
      onChange([]);
    }
    this.setState({
      value: [],
      ...getPanelDate([]),
    });
  };

  handleStartPanelDateChange = value => {
    this.setState({
      startPanelDate: value,
    });
  };

  handleEndPanelDateChange = value => {
    this.setState(state => ({
      endPanelDate: moment(state.startPanelDate).isSameOrAfter(moment(value), 'month')
        ? value.add(1, 'month')
        : value,
    }));
  };

  startDisabledMonth = date => {
    const { endPanelDate } = this.state;
    return date >= moment(endPanelDate).startOf('month');
  };

  endDisabledMonth = date => {
    const { startPanelDate } = this.state;
    return date <= moment(startPanelDate).endOf('month');
  };

  startDisabledYear = date => {
    const { value } = this.state;
    return date.isAfter(moment(value[1]), 'year');
  };

  endDisabledYear = date => {
    const { value } = this.state;
    return date.isBefore(moment(value[0]), 'year');
  };

  startDisabledDate = date => {
    const { disabledDate } = this.props;
    const { endPanelDate } = this.state;
    return date > moment(endPanelDate).startOf('month') || (disabledDate && disabledDate(date));
  };

  endDisabledDate = date => {
    const { disabledDate } = this.props;
    const { startPanelDate } = this.state;
    return date < moment(startPanelDate).endOf('month') || (disabledDate && disabledDate(date));
  };

  getInputRef = el => {
    this.inputRef = el;
  };

  render() {
    const {
      prefixCls,
      format,
      disabled,
      inputProps,
      size,
      readOnly,
      separator,
      showTime,
      status,
      clearable,
      onChange,
      clearIcon,
      errorIcon,
      loadingIcon,
      showStatusIcon,
      suffix,
      disabledDate,
      successIcon,
      prefix,
      scrollContainer,
      placeholder,
      ...other
    } = this.props;

    const { value, startPanelDate, endPanelDate, panelType } = this.state;

    return (
      <Tooltip
        position="bottom"
        onClose={this.handleClose}
        className={prefixCls}
        trigger="click"
        autoAdjustPosition
        scrollContainer={scrollContainer}
        content={
          <Input
            aria-haspopup="true"
            prefix={prefix}
            className={`${prefixCls}-input-container`}
            disabled={disabled}
            clearable={clearable}
            onClear={this.handleClear}
            clearIcon={clearIcon}
            errorIcon={errorIcon}
            loadingIcon={loadingIcon}
            suffix={suffix}
            inputRef={this.getInputRef}
            successIcon={successIcon}
            status={status}
            showStatusIcon={showStatusIcon}
            value={value[0] ? value[0].format(format) : ''}
            {...inputProps}
            mask={
              <div className={`${prefixCls}-input`}>
                <span className={`${prefixCls}-input-base ${prefixCls}-input-base-begin`}>
                  {value[0] ? value[0].format(format) : placeholder && placeholder[0]}
                </span>
                <span className={`${prefixCls}-input-seperator`}>{separator}</span>
                <span className={`${prefixCls}-input-base ${prefixCls}-input-base-end`}>
                  {value[1] ? value[1].format(format) : placeholder && placeholder[1]}
                </span>
              </div>
            }
            size={size}
          />
        }>
        <div className={`${prefixCls}-calendar-unity`}>
          <Calendar
            {...other}
            endPanelDate={endPanelDate} // forceUpdate
            panelType={panelType}
            onPanelTypeChange={this.handlePanelTypeChange}
            onPanelDateChange={this.handleStartPanelDateChange}
            disabledMonth={this.startDisabledMonth}
            disabledYear={this.startDisabledYear}
            rangeType="start"
            value={value}
            disabledDate={this.startDisabledDate}
            onValueChange={this.handleValueChange}
            onTimeChange={this.handleTimeChange}
            panelDate={startPanelDate}
          />
          <Calendar
            {...other}
            startPanelDate={startPanelDate} // forceUpdate
            panelType={panelType}
            disabledMonth={this.endDisabledMonth}
            disabledYear={this.endDisabledYear}
            rangeType="end"
            value={value}
            onPanelDateChange={this.handleEndPanelDateChange}
            onPanelTypeChange={this.handlePanelTypeChange}
            onValueChange={this.handleValueChange}
            onTimeChange={this.handleTimeChange}
            disabledDate={this.endDisabledDate}
            panelDate={endPanelDate}
          />
        </div>
        {value &&
        value.length === 2 &&
        showTime &&
        (panelType === 'date' || panelType === 'time') ? (
          <div className={`${prefixCls}-footer`}>
            {panelType === 'date' ? (
              <Button variant="text" onClick={() => this.handlePanelTypeChange('time')}>
                时间
              </Button>
            ) : panelType === 'time' ? (
              <Button variant="text" onClick={() => this.handlePanelTypeChange('date')}>
                日期
              </Button>
            ) : null}
          </div>
        ) : null}
      </Tooltip>
    );
  }
}

export default DateRangePicker;



// WEBPACK FOOTER //
// ./src/datePicker/DateRangePicker.jsx